// 存档持久化接线（EP-002-S7）—— 订阅 store 变化，节流 200ms 写 localStorage。
// 仅当 `save` 引用真正改变时才排程写入（clock 等渲染用字段变化不触发写，符合 GDD 02 §④-4.3
// 「只在四个时机写」的精神：出发 / 掉落生成 / 结算 / 设置变更）。
//
// ★ EP-010-S3 基础同步接入（P13-SYNC-WIRE）—— 在这里**追加**云端同步触发器：
//   - 现有节流逻辑（schedulePersist / persistNow / flush / timer）一行不改
//   - 新增独立变量 cloudTimer 与新函数 scheduleCloudSync，与本地写盘 timer 完全隔离
//   - 在 subscribe 回调里追加一行 `scheduleCloudSync()`，触发条件与 schedulePersist **相同**
//   - 调用 uploadToCloud 内部通过 GET/PUT 走 §v3-J 真相源原则（src/api/client.ts）
import { useGameStore } from './index';
import { persist as writeSave } from '../save';
import type { StorageAdapter } from '../save/storage';
import type { SaveDataV1 } from '../save/types';

let adapter: StorageAdapter | null = null;
let timer: ReturnType<typeof setTimeout> | null = null;
let pending: SaveDataV1 | null = null;

// ★ EP-010-S3：云端同步独立 timer（与本地持久化 timer 完全隔离，互不干扰）。
//   设这个为模块级变量（不导出）—— 既不污染 store 字段，也与 setAdapter / 适配器解耦
//   —— 单元测试时可手动推进 fake timers 触发上传，与本地 persist 节流互不影响。
let cloudTimer: ReturnType<typeof setTimeout> | null = null;

// 云端上传 debounce 间隔（任务 P13-SYNC-WIRE 指定 1–2s；选 1500ms）。
//   · 一次业务操作可能触发多次 setState（settle 入库 + clock bump 等），1.5s 合批成 1 个 PUT；
//   · §v3-G / §v3-A.5「Lambda 冷启动 0.5–2s」是已知成本，此处不与冷启动争带宽；
//   · 间隔由 S6 / S7 接管时再细化（任务约束「只做基础接入」）。
const CLOUD_DEBOUNCE_MS = 1500;

// 设置当前使用的存储适配器（由 store.init 探测后注入）。
export function setAdapter(a: StorageAdapter): void {
  adapter = a;
}

// 立即写（init 首次落盘 / 重置用，不节流）。
export function persistNow(save: SaveDataV1): void {
  if (!adapter) return;
  if (timer) {
    clearTimeout(timer);
    timer = null;
  }
  writeSave(save, adapter);
  pending = null;
}

function flush(): void {
  if (adapter && pending) writeSave(pending, adapter);
  pending = null;
  timer = null;
}

// 订阅 store：save 变化时节流 200ms 写盘。立即写（清计时器）确保最新值不丢。
function schedulePersist(save: SaveDataV1): void {
  pending = save;
  if (timer) return;
  timer = setTimeout(() => {
    flush();
  }, 200);
}

// 暴露给 store.init 调用：建立订阅。
export function subscribeToStore(): void {
  useGameStore.subscribe((state, prev) => {
    if (state.save !== prev.save && state.save) {
      schedulePersist(state.save); // ★ 既有节流逻辑（200ms 写 localStorage）—— 本任务一行不改
      scheduleCloudSync();          // ★ EP-010-S3 追加（1500ms debounce 上传云端，与上互不干扰）
    }
  });
}

/**
 * ★ EP-010-S3 基础同步接入：触发云端同步（debounce）。
 *
 * 行为：
 *   - 与 schedulePersist 共享触发条件（`save !== prev.save && state.save`），
 *     **不**重置已有 timer（标准 debounce 语义：连续的 setState 只产生 1 个 PUT）。
 *   - timer 内调用 `useGameStore.getState().uploadToCloud()`：
 *     · 200 → store 把 cloudRev 升到云端返回值
 *     · 401 → markLoginRequired（§v3-C.5）
 *     · 409 → store 取云端覆盖本地 + console.warn 标记 S6 接管
 *     · 5xx / network → store 静默（§v3-H.4 由 S7 接入）
 *
 * 边界：本函数只调度，**不**直接调 fetch / 写 localStorage。
 * 失败由 `uploadToCloud` 内部处理；这里再 catch 一次仅做双保险（理论上不应触发）。
 */
function scheduleCloudSync(): void {
  if (cloudTimer) return; // 已有未触发的 timer → 不重置（debounce）
  cloudTimer = setTimeout(() => {
    cloudTimer = null;
    // useGameStore.getState() 拿的是最新（含 init 后 fire-and-forget 的 applyCloudSave），
    // 不读 prev 不会错过事件。
    void (async () => {
      try {
        await useGameStore.getState().uploadToCloud();
      } catch {
        // uploadToCloud 已 try/catch 业务错误；这里兜住意外 throw 即可
      }
    })();
  }, CLOUD_DEBOUNCE_MS);
}
